import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, Plus, Trash2, Send, Building2 } from "lucide-react";
import ConditioningSuggester from "../ConditioningSuggester";

const ShoppingListModal = ({
  open,
  onOpenChange,
  shoppingList,
  setShoppingList,
  shoppingListSupplier,
  setShoppingListSupplier,
  shoppingListDate,
  setShoppingListDate,
  newListItem,
  setNewListItem,
  formatPrice,
  getShoppingListTotal,
  addToShoppingList,
  removeFromShoppingList,
  submitShoppingList,
}) => {
  const handleOpenChange = (isOpen) => {
    if (!isOpen && shoppingList.length > 0) {
      if (!window.confirm("Vous avez des articles dans la liste. Voulez-vous vraiment fermer ?")) return;
    }
    onOpenChange(isOpen);
  };

  const groupsMap = (() => {
    const groups = {};
    shoppingList.forEach((item) => {
      const key = (item.supplier || "").trim() || "__NO_SUPPLIER__";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  })();

  const nbGroups = Object.keys(groupsMap).length;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="shopping-list-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-indigo-400 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Achats Fournisseurs
            {shoppingList.length > 0 && (
              <Badge className="bg-indigo-500/30 text-indigo-300 ml-2">
                {shoppingList.length} article(s) • Total: {formatPrice(getShoppingListTotal())} F
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 bg-slate-700/30 rounded-lg p-3">
            <div>
              <Label className="text-slate-300 text-sm">Fournisseur par défaut</Label>
              <Input
                value={shoppingListSupplier}
                onChange={(e) => setShoppingListSupplier(e.target.value)}
                placeholder="Pré-rempli pour chaque nouvel article"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">Date prévue (commune)</Label>
              <Input
                type="date"
                value={shoppingListDate}
                onChange={(e) => setShoppingListDate(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>
            <div className="col-span-2 text-xs text-indigo-300 bg-indigo-900/20 rounded p-2">
              💡 Une demande d'achat distincte sera créée <b>par fournisseur</b>. Renseignez le fournisseur directement sur chaque article si besoin.
            </div>
          </div>

          <Card className="bg-indigo-900/20 border-indigo-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-indigo-300 text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Ajouter un article
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                <Select value={newListItem.category} onValueChange={(v) => setNewListItem({ ...newListItem, category: v })}>
                  <SelectTrigger className="w-full sm:w-[120px] bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="cuisine">🍳 Cuisine</SelectItem>
                    <SelectItem value="bar">🍹 Bar</SelectItem>
                    <SelectItem value="paiement">💳 Paiement</SelectItem>
                    <SelectItem value="autres">📦 Autres</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={newListItem.description}
                  onChange={(e) => setNewListItem({ ...newListItem, description: e.target.value })}
                  placeholder="Libellé article"
                  className="flex-1 min-w-[150px] bg-slate-700/50 border-slate-600 text-white"
                />
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={newListItem.quantity || ""}
                  onChange={(e) => setNewListItem({ ...newListItem, quantity: parseFloat(e.target.value.replace(",", ".")) || 1 })}
                  placeholder="Qté"
                  className="w-full sm:w-[70px] bg-slate-700/50 border-slate-600 text-white"
                />
                <Input
                  type="number"
                  value={newListItem.unit_price || ""}
                  onChange={(e) => setNewListItem({ ...newListItem, unit_price: parseFloat(e.target.value) || 0 })}
                  placeholder="Prix (opt.)"
                  className="w-full sm:w-[100px] bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
              <div className="flex gap-2 mt-2 items-center">
                <Input
                  value={newListItem.supplier || ""}
                  onChange={(e) => setNewListItem({ ...newListItem, supplier: e.target.value })}
                  placeholder={`Fournisseur de l'article${shoppingListSupplier ? ` (défaut : ${shoppingListSupplier})` : ""}`}
                  className="flex-1 bg-slate-700/50 border-slate-600 text-white"
                  data-testid="list-item-supplier"
                />
                <div className="flex items-center bg-indigo-900/30 rounded px-2 text-indigo-300 text-sm shrink-0">
                  = {formatPrice((newListItem.quantity || 1) * (newListItem.unit_price || 0))} F
                </div>
                <Button onClick={addToShoppingList} className="bg-indigo-600 hover:bg-indigo-700 shrink-0">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <ConditioningSuggester
                description={newListItem.description}
                category={newListItem.category}
                quantity={newListItem.quantity}
                onApply={(desc, qty) => setNewListItem({ ...newListItem, description: desc, quantity: qty })}
                testIdPrefix="list"
              />
              <p className="text-xs text-slate-500 mt-2">
                💡 Prix optionnel — laissez vide si inconnu. Fournisseur optionnel (utilise le fournisseur par défaut ci-dessus).
              </p>
            </CardContent>
          </Card>

          {shoppingList.length > 0 ? (
            <Card className="bg-slate-700/30 border-slate-600">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-300 text-sm">Articles groupés par fournisseur</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[300px] overflow-y-auto">
                {Object.entries(groupsMap).map(([supKey, items]) => {
                  const supLabel = supKey === "__NO_SUPPLIER__" ? "Sans fournisseur" : supKey;
                  const groupTotal = items.reduce((s, it) => s + (it.amount || 0), 0);
                  return (
                    <div key={supKey} className="bg-slate-800/40 rounded-lg p-2 border-l-2 border-indigo-500/60">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3 h-3 text-indigo-400" />
                          <span className="text-indigo-300 font-bold text-sm">{supLabel}</span>
                          <Badge className="bg-slate-700/50 text-slate-300 text-[10px]">
                            {items.length} article(s)
                          </Badge>
                        </div>
                        <span className="text-indigo-300 font-bold text-sm">{formatPrice(groupTotal)} F</span>
                      </div>
                      <div className="space-y-1">
                        {items.map((item) => (
                          <div key={item.id} className="flex items-center justify-between gap-2 bg-slate-700/30 rounded p-1.5">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Badge
                                className={`text-[10px] shrink-0 ${
                                  item.category === "cuisine"
                                    ? "bg-green-500/20 text-green-400"
                                    : item.category === "bar"
                                    ? "bg-orange-500/20 text-orange-400"
                                    : item.category === "paiement"
                                    ? "bg-blue-500/20 text-blue-400"
                                    : "bg-slate-500/20 text-slate-400"
                                }`}
                              >
                                {item.category}
                              </Badge>
                              <span className="text-white text-sm truncate">{item.description}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-slate-400 text-xs">
                                {item.quantity || 1} × {formatPrice(item.unit_price)} ={" "}
                              </span>
                              <span className="text-amber-400 font-bold text-sm">{formatPrice(item.amount)} F</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => removeFromShoppingList(item.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-6 w-6 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Aucun article dans la liste</p>
              <p className="text-sm">Ajoutez des articles ci-dessus</p>
            </div>
          )}

          {shoppingList.length > 0 && (
            <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-lg p-4 border border-indigo-500/30">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-300">
                  {nbGroups} fournisseur(s) • {shoppingList.length} article(s) • Total :
                </span>
                <span className="text-2xl font-bold text-indigo-400">{formatPrice(getShoppingListTotal())} F</span>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={submitShoppingList}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="shopping-list-submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Soumettre {nbGroups} demande(s) d'achat
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (window.confirm("Vider toute la liste ?")) {
                      setShoppingList([]);
                    }
                  }}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full border-slate-600 text-slate-400"
            data-testid="shopping-list-close-btn"
          >
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShoppingListModal;
